import { useQuery } from '@tanstack/react-query'
import { Eye, EyeOff, Globe, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime } from '../../../shared/format'
import type { Meeting } from '../../types'
import { useCrm } from '../../ui'

const ICON = { account: Globe, restricted: Users, private: EyeOff } as const

/** Görünürlük etiketi ve (sahip ya da yetkiliye) görüntüleme geçmişi. */
export function MeetingVisibilityBar({ meeting: m }: { meeting: Meeting }) {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const [open, setOpen] = useState(false)
  const Icon = ICON[m.visibility]
  const canLog = !m.restricted && (can('crm:meeting:view_log') || user?.id === m.ownerId)
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs">
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Icon className="size-3.5" aria-hidden />
        {t(`crm.meeting.visibilities.${m.visibility}`)}
      </span>
      {canLog ? (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen(true)}>
          <Eye />
          {t('crm.meeting.viewLog')}
        </Button>
      ) : null}
      {open ? <ViewsModal meetingId={m.id} onClose={() => setOpen(false)} /> : null}
    </div>
  )
}

function ViewsModal({ meetingId, onClose }: { meetingId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  const q = useQuery({
    queryKey: ['crm', 'meeting-views', meetingId],
    queryFn: async () =>
      asList(await api.get<Record<string, unknown>[]>(`/api/v1/meetings/${meetingId}/views`)).map((r) => ({ userId: String(r.user_id ?? ''), at: String(r.at ?? '') })),
  })
  return (
    <Modal open size="md" onOpenChange={(v) => !v && onClose()} icon={<Eye />} title={t('crm.meeting.viewLog')} description={t('crm.meeting.viewLogHint')}>
      {!q.data ? (
        <Skeleton className="h-24" />
      ) : !q.data.length ? (
        <p className="text-sm text-muted-foreground">{t('crm.meeting.noViews')}</p>
      ) : (
        <ul className="divide-y text-sm">
          {q.data.map((v, i) => (
            <li key={i} className="flex justify-between gap-2 py-2">
              <span className="font-medium">{staffName(v.userId)}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{fmtDateTime(v.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
